import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, Ban, ExternalLink } from 'lucide-react';
import TransactionOverlay from '../../components/transactions/TransactionOverlay';
import VoidConfirmationModal from '../../components/VoidConfirmationModal';
import {
  getItemStartingValue,
  voidItemStartingValue,
} from '../../utils/inventoryStartingValueApi';
import { getItemById } from '../../utils/itemApi';
import { ApiValidationError } from '../../types/auth';
import type { InventoryStartingValue } from '../../types/inventoryStartingValue';
import type { Item } from '../../types/item';
import { formatCurrency, formatPreviewQuantity } from '../../utils/formatCurrency';
import { toast } from '../../utils/toast';
import { startingValueVoidCopy } from '../../utils/voidConfirmationCopy';
import {
  canSetStartingValue,
  canVoidStartingValue,
  formatStartingValueDate,
  formatStartingValueLabel,
  formatStartingValueTime,
  getStartingValueStatusColor,
} from '../../utils/inventoryStartingValueForm';
import { useInventoryTransactionsHub } from './InventoryTransactionsHubContext';

const InventoryStartingValuePreview = () => {
  const { itemId: itemIdParam } = useParams<{ itemId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { refetchList } = useInventoryTransactionsHub();
  const listSearch = location.search.replace(/^\?/, '');

  const itemId = itemIdParam != null ? Number(itemIdParam) : Number.NaN;

  const [item, setItem] = useState<Item | null>(null);
  const [startingValue, setStartingValue] = useState<InventoryStartingValue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notSet, setNotSet] = useState(false);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [voidError, setVoidError] = useState<string | null>(null);

  const close = useCallback(() => {
    navigate({ pathname: '/inventory-transactions', search: listSearch }, { preventScrollReset: true });
  }, [listSearch, navigate]);

  const loadDetail = useCallback(async () => {
    if (!itemIdParam || Number.isNaN(itemId)) {
      setError('Invalid item ID');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setNotSet(false);

      const [itemData, sv] = await Promise.all([
        getItemById(itemId),
        getItemStartingValue(itemId),
      ]);

      setItem(itemData);
      if (!sv) {
        setNotSet(true);
        setStartingValue(null);
      } else {
        setStartingValue(sv);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load opening stock');
      setItem(null);
      setStartingValue(null);
    } finally {
      setLoading(false);
    }
  }, [itemId, itemIdParam]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  const openVoidModal = () => {
    setVoidError(null);
    setShowVoidModal(true);
  };

  const cancelVoidModal = () => {
    setShowVoidModal(false);
    setVoidError(null);
  };

  const confirmVoid = async (reason: string) => {
    if (!canVoidStartingValue() || !startingValue) return;

    try {
      setVoiding(true);
      setVoidError(null);
      const voided = await voidItemStartingValue(itemId, {
        void_reason: reason || null,
      });
      setShowVoidModal(false);
      setStartingValue(voided);
      refetchList();
      toast.success(`Opening stock voided for ${voided.item.name}.`);
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setVoidError(err.message);
      } else {
        toast.error(err instanceof Error ? err.message : 'Failed to void opening stock');
      }
    } finally {
      setVoiding(false);
    }
  };

  const canVoidDetail =
    startingValue != null && startingValue.status !== 'VOIDED' && canVoidStartingValue();

  return (
    <>
    <TransactionOverlay
      variant="preview"
      title={startingValue ? startingValue.item.name : item?.name ?? 'Opening stock'}
      subtitle={
        startingValue
          ? `Opening stock · ${formatStartingValueLabel(startingValue.status)}`
          : item
            ? `${item.sku} · Opening stock`
            : 'Loading…'
      }
      onClose={close}
    >
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-800" />
        </div>
      ) : error ? (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => void loadDetail()} className="underline shrink-0">
            Retry
          </button>
        </div>
      ) : notSet && item ? (
        <div className="space-y-4 text-center py-4">
          <p className="text-sm text-gray-600">No opening stock set for this item.</p>
          <p className="text-xs text-gray-500 font-mono">
            {item.name} ({item.sku})
          </p>
          {canSetStartingValue() && (
            <Link
              to={`/manage-inventory-starting-values?itemId=${item.id}`}
              className="inline-flex px-4 py-2 bg-slate-800 text-white rounded-lg text-sm hover:bg-slate-700"
            >
              Set opening stock
            </Link>
          )}
          <button
            type="button"
            onClick={close}
            className="block mx-auto px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm"
          >
            Close
          </button>
        </div>
      ) : startingValue ? (
        <div className="space-y-5">
          <div className="flex flex-wrap gap-2">
            <span
              className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${getStartingValueStatusColor(startingValue.status)}`}
            >
              {formatStartingValueLabel(startingValue.status)}
            </span>
          </div>

          <div className="grid grid-cols-1 gap-3 text-sm bg-gray-50 rounded-lg p-4">
            <div>
              <span className="text-gray-500 block text-xs">SKU</span>
              <span className="font-mono">{startingValue.item.sku}</span>
            </div>
            <div>
              <span className="text-gray-500 block text-xs">As-of date</span>
              <span>{formatStartingValueDate(startingValue.as_of_date)}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="text-gray-500 block text-xs">Quantity</span>
                <span className="font-mono">{formatPreviewQuantity(startingValue.qty)}</span>
              </div>
              <div>
                <span className="text-gray-500 block text-xs">Unit cost</span>
                <span className="font-mono">{formatCurrency(startingValue.unit_cost)}</span>
              </div>
            </div>
            <div>
              <span className="text-gray-500 block text-xs">Total value</span>
              <span className="font-semibold font-mono">
                {formatCurrency(startingValue.total_value)}
              </span>
            </div>
            {startingValue.equity_account && (
              <div>
                <span className="text-gray-500 block text-xs">Equity account</span>
                <span>
                  {startingValue.equity_account.account_number} —{' '}
                  {startingValue.equity_account.name}
                </span>
              </div>
            )}
            {startingValue.memo && (
              <div>
                <span className="text-gray-500 block text-xs">Memo</span>
                <span>{startingValue.memo}</span>
              </div>
            )}
            <div>
              <span className="text-gray-500 block text-xs">Created</span>
              <span>
                {formatStartingValueDate(startingValue.created_at)}{' '}
                {formatStartingValueTime(startingValue.created_at)}
              </span>
            </div>
            {startingValue.status === 'VOIDED' && startingValue.void_reason && (
              <div>
                <span className="text-gray-500 block text-xs">Void reason</span>
                <span>{startingValue.void_reason}</span>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200">
            <Link
              to={`/inventory-starting-values/${startingValue.item_id}`}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50"
            >
              <ExternalLink className="h-4 w-4" />
              Open full details
            </Link>
            {canVoidDetail && (
              <button
                type="button"
                onClick={openVoidModal}
                disabled={voiding}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm text-red-700 border border-red-200 rounded-lg hover:bg-red-50"
              >
                <Ban className="h-4 w-4" />
                Void
              </button>
            )}
            <button
              type="button"
              onClick={close}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm ml-auto"
            >
              Close
            </button>
          </div>
        </div>
      ) : null}
    </TransactionOverlay>

    {startingValue && (
      <VoidConfirmationModal
        isOpen={showVoidModal}
        {...startingValueVoidCopy(startingValue.item.name)}
        confirmText="Void opening stock"
        showReasonField
        isSubmitting={voiding}
        error={voidError}
        onCancel={cancelVoidModal}
        onConfirm={confirmVoid}
      />
    )}
    </>
  );
};

export default InventoryStartingValuePreview;
